import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { Send, Trash2, Search, Clock, Users, Eye, UserCog } from 'lucide-react';
import EnviarVendedorModal from '../components/EnviarVendedorModal';

const S = {
  heading: { fontSize:22, fontWeight:700, color:'#f6c90e', marginBottom:4 },
  sub: { fontSize:13, color:'#475569', marginBottom:24 },
  searchWrap: { position:'relative', maxWidth:420, marginBottom:24 },
  searchBox: { display:'flex', alignItems:'center', gap:8, background:'#0e1420', border:'1px solid #1a2035', borderRadius:8, padding:'10px 14px' },
  searchInput: { background:'none', border:'none', color:'#e2e8f0', fontSize:14, outline:'none', flex:1 },
  dropdown: { position:'absolute', top:'100%', left:0, right:0, background:'#0e1420', border:'1px solid #1a2035', borderRadius:8, zIndex:200, maxHeight:260, overflowY:'auto', marginTop:4 },
  dropItem: { padding:'10px 14px', cursor:'pointer', borderBottom:'1px solid #0f1117', display:'flex', justifyContent:'space-between', alignItems:'center' },
  dropEmpty: { padding:'12px 14px', fontSize:13, color:'#475569' },
  tabBar: { display:'flex', gap:4, marginBottom:20, borderBottom:'1px solid #1a2035' },
  tab: (active) => ({
    padding:'9px 20px', fontSize:13, fontWeight:600, cursor:'pointer', border:'none', background:'none',
    color: active ? '#f6c90e' : '#475569',
    borderBottom: active ? '2px solid #f6c90e' : '2px solid transparent',
    marginBottom: -1,
    display:'flex', alignItems:'center', gap:6,
  }),
  badge: (c='#f6c90e') => ({
    background:`${c}22`, border:`1px solid ${c}44`, borderRadius:10,
    padding:'1px 7px', fontSize:11, fontWeight:700, color:c,
  }),
  dateGroup: { marginBottom:24 },
  dateLabel: { fontSize:11, color:'#64748b', textTransform:'uppercase', letterSpacing:1, fontWeight:700, marginBottom:10, paddingBottom:6, borderBottom:'1px solid #1a2035' },
  card: { background:'#0e1420', border:'1px solid #1a2035', borderRadius:12, padding:'14px 18px', marginBottom:10, display:'flex', alignItems:'center', justifyContent:'space-between', gap:16, flexWrap:'wrap' },
  clienteNome: { fontSize:14, fontWeight:700, color:'#e2e8f0' },
  clienteNomeH3: { fontSize:16, fontWeight:700, color:'#f6c90e', margin:0 },
  metaRow: { display:'flex', gap:18, flexWrap:'wrap', marginTop:6 },
  metaItem: { fontSize:12, color:'#94a3b8' },
  metaLbl: { color:'#475569' },
  actions: { display:'flex', gap:8, flexShrink:0 },
  actionBtn: (c) => ({ background:`${c}22`, border:`1px solid ${c}44`, borderRadius:7, padding:'7px 12px', cursor:'pointer', color:c, display:'flex', alignItems:'center', gap:6, fontSize:12, fontWeight:600 }),
  empty: { color:'#334155', fontSize:13, textAlign:'center', padding:'40px 0', fontStyle:'italic' },
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    const str = typeof iso === 'string' && !iso.includes('T') ? iso + 'T12:00:00' : iso;
    return new Date(str).toLocaleDateString('pt-BR');
  } catch { return '—'; }
};

const fmtDateTime = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('pt-BR') + ' ' + d.toLocaleTimeString('pt-BR', { hour:'2-digit', minute:'2-digit' });
  } catch { return '—'; }
};

const dateKey = (iso) => {
  if (!iso) return '0000-00-00';
  const d = new Date(iso);
  if (isNaN(d)) return '0000-00-00';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

// Agrupa uma lista de leads por dia (usando dateField), ordenado do mais recente para o mais antigo
function groupByDate(list, dateField) {
  const groups = {};
  list.forEach(item => {
    const key = dateKey(item[dateField]);
    if (!groups[key]) groups[key] = [];
    groups[key].push(item);
  });
  return Object.keys(groups)
    .sort((a, b) => b.localeCompare(a))
    .map(key => ({ key, dateLabel: fmtDate(key), items: groups[key] }));
}

export default function PreVendaUltimasAtualizacoes() {
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [loadErro, setLoadErro] = useState('');
  const [activeTab, setActiveTab] = useState('cadastrados');

  const [search, setSearch] = useState('');
  const [showSearchDrop, setShowSearchDrop] = useState(false);
  const searchRef = useRef(null);

  const [editing, setEditing] = useState(null);

  const load = () => {
    setLoadErro('');
    api.get('/pre-venda/meus-leads/').then(r => setLeads(r.data)).catch(() => setLoadErro('Erro ao carregar dados. Tente atualizar a página.'));
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    const handler = (e) => { if (searchRef.current && !searchRef.current.contains(e.target)) setShowSearchDrop(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const cadastrados = useMemo(() => leads.filter(l => l.status === 'pre_venda'), [leads]);
  const enviados     = useMemo(() => leads.filter(l => l.status === 'aguardando_vendedor'), [leads]);

  const searchResults = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return [];
    return leads.filter(l => l.cliente_nome?.toLowerCase().includes(term)).slice(0, 8);
  }, [search, leads]);

  const goToJornada = (lead) => {
    if (!lead?.cliente_id) return;
    setShowSearchDrop(false);
    setSearch('');
    navigate(`/jornada/${lead.cliente_id}`);
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter' && searchResults.length > 0) {
      e.preventDefault();
      goToJornada(searchResults[0]);
    }
  };

  const handleDelete = async (lead) => {
    if (!window.confirm(`Excluir o lead de ${lead.cliente_nome}? Esta ação não pode ser desfeita.`)) return;
    try {
      await api.delete(`/pre-venda/meus-leads/${lead.id}/`);
      load();
    } catch (e) { alert('Erro ao excluir: ' + e.message); }
  };

  const cadastradosGrupos = useMemo(() => groupByDate(cadastrados, 'criado_em'), [cadastrados]);
  const enviadosGrupos    = useMemo(() => groupByDate(enviados, 'atribuido_vendedor_em'), [enviados]);

  return (
    <div>
      <div style={S.heading}>Últimas Atualizações</div>
      <div style={S.sub}>Busque um cliente para abrir a jornada, ou acompanhe os leads recentes por data</div>

      {loadErro && (
        <div style={{ background:'rgba(248,113,113,0.1)', border:'1px solid rgba(248,113,113,0.3)', borderRadius:8, padding:'10px 14px', color:'#f87171', fontSize:13, marginBottom:14 }}>{loadErro}</div>
      )}

      {/* Busca de cliente (autocomplete) */}
      <div style={S.searchWrap} ref={searchRef}>
        <div style={S.searchBox}>
          <Search size={15} color="#475569" />
          <input
            style={S.searchInput}
            placeholder="Buscar cliente pelo nome..."
            value={search}
            onChange={e => { setSearch(e.target.value); setShowSearchDrop(true); }}
            onFocus={() => setShowSearchDrop(true)}
            onKeyDown={handleSearchKeyDown}
          />
        </div>
        {showSearchDrop && search.trim() && (
          <div style={S.dropdown}>
            {searchResults.length === 0 ? (
              <div style={S.dropEmpty}>Nenhum cliente encontrado</div>
            ) : searchResults.map(l => (
              <div key={l.id} style={S.dropItem}
                onClick={() => goToJornada(l)}
                onMouseEnter={e => e.currentTarget.style.background = '#111827'}
                onMouseLeave={e => e.currentTarget.style.background = ''}>
                <span style={{ fontSize:13, color:'#e2e8f0', fontWeight:600 }}>{l.cliente_nome}</span>
                <span style={{ fontSize:11, color:'#475569' }}>{l.cliente_cidade || ''}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Abas */}
      <div style={S.tabBar}>
        <button style={S.tab(activeTab === 'cadastrados')} onClick={() => setActiveTab('cadastrados')}>
          <Clock size={14} />
          Últimos Leads Cadastrados
          {cadastrados.length > 0 && <span style={S.badge()}>{cadastrados.length}</span>}
        </button>
        <button style={S.tab(activeTab === 'enviados')} onClick={() => setActiveTab('enviados')}>
          <Users size={14} />
          Últimos Leads Enviados
          {enviados.length > 0 && <span style={S.badge('#4ade80')}>{enviados.length}</span>}
        </button>
      </div>

      {/* ── Últimos Leads Cadastrados ─────────────────────────────────────── */}
      {activeTab === 'cadastrados' && (
        cadastradosGrupos.length === 0 ? (
          <div style={S.empty}>Nenhum lead cadastrado ainda.</div>
        ) : cadastradosGrupos.map(grupo => (
          <div key={grupo.key} style={S.dateGroup}>
            <div style={S.dateLabel}>{grupo.dateLabel}</div>
            {grupo.items.map(l => (
              <div key={l.id} style={S.card}>
                <div>
                  <div style={S.clienteNome}>{l.cliente_nome}</div>
                  <div style={S.metaRow}>
                    <span style={S.metaItem}><span style={S.metaLbl}>Cadastrado em: </span>{fmtDateTime(l.criado_em)}</span>
                    {l.cliente_cidade && <span style={S.metaItem}><span style={S.metaLbl}>Cidade: </span>{l.cliente_cidade}</span>}
                  </div>
                </div>
                <div style={S.actions}>
                  <button style={S.actionBtn('#f6c90e')} onClick={() => setEditing(l)}>
                    <Send size={13} /> Enviar Para Vendedor
                  </button>
                  <button style={S.actionBtn('#f87171')} onClick={() => handleDelete(l)}>
                    <Trash2 size={13} /> Excluir
                  </button>
                </div>
              </div>
            ))}
          </div>
        ))
      )}

      {/* ── Últimos Leads Enviados ────────────────────────────────────────── */}
      {activeTab === 'enviados' && (
        enviadosGrupos.length === 0 ? (
          <div style={S.empty}>Nenhum lead enviado para vendedor ainda.</div>
        ) : enviadosGrupos.map(grupo => (
          <div key={grupo.key} style={S.dateGroup}>
            <div style={S.dateLabel}>{grupo.dateLabel}</div>
            {grupo.items.map(l => (
              <div key={l.id} style={S.card}>
                <div>
                  <h3 style={S.clienteNomeH3}>{l.cliente_nome}</h3>
                  <div style={S.metaRow}>
                    <span style={S.metaItem}><span style={S.metaLbl}>Vendedor: </span>{l.vendedor_nome || '—'}</span>
                    <span style={S.metaItem}><span style={S.metaLbl}>Enviado em: </span>{fmtDateTime(l.atribuido_vendedor_em)}</span>
                  </div>
                </div>
                <div style={S.actions}>
                  <button style={S.actionBtn('#60a5fa')} onClick={() => navigate(`/jornada/${l.cliente_id}`)}>
                    <Eye size={13} /> Ver/Editar
                  </button>
                  <button style={S.actionBtn('#a78bfa')} onClick={() => setEditing(l)}>
                    <UserCog size={13} /> Alterar Vendedor
                  </button>
                </div>
              </div>
            ))}
          </div>
        ))
      )}

      {/* ── Modal Enviar / Alterar Vendedor ─────────────────────────────────── */}
      {editing && (
        <EnviarVendedorModal
          lead={editing}
          onClose={() => setEditing(null)}
          onSaved={load}
        />
      )}
    </div>
  );
}
